import { useId, useState } from 'react';
import { formatBRL, type DiaDoRelatorio } from '../api.js';

/**
 * Série diária: comissão e conversões.
 *
 * Três decisões que a forma impõe, e o porquê de cada uma:
 *
 * 1. DOIS gráficos, não um com dois eixos. Comissão (reais) e conversões
 *    (contagem) têm escalas sem relação; sobrepor as duas num eixo faria a
 *    linha de uma parecer explicar a outra. Eixos gêmeos são o erro mais comum
 *    em painel — aqui viram múltiplos pequenos, empilhados e com o MESMO x.
 *
 * 2. BARRA, não linha. Venda é evento discreto: em 30 dias, a maioria fica em
 *    zero. Uma linha ligando os dias com venda desenharia uma continuidade que
 *    não existe, e o zero viraria uma diagonal em vez de ausência.
 *
 * 3. CANCELADA por TEXTURA, não por cor. Verde e vermelho na mesma
 *    luminosidade têm separação ΔE ~1–7 para daltonismo deutan (medido, não
 *    estimado) — quem não distingue as duas veria uma barra só. Como cancelada
 *    é a NEGAÇÃO de uma venda, e não uma categoria irmã, ela aparece hachurada:
 *    funciona em qualquer visão, e diz "isto não contou" melhor que vermelho.
 */

type Props = {
  series: DiaDoRelatorio[];
  dias: number;
};

/**
 * Geometria em unidades de viewBox; o SVG escala sozinho na largura.
 *
 * TOPO tem folga suficiente para o título da série ficar ACIMA da primeira
 * linha de grade. Com folga menor, o rótulo do eixo ("16,99") batia em cima do
 * título ("Comissão") — o validador de paleta não pega isso, só olhar pega.
 */
const ALTURA = 120;
const TITULO_Y = 9;
const TOPO = 26;
const BASE = ALTURA - 22;
const EIXO_X = 46; // espaço do rótulo do eixo y

/**
 * Preferência de recolhido/expandido, por navegador.
 *
 * Guardada porque é decisão de leitura, não dado: quem expande uma vez não quer
 * expandir de novo a cada visita. Fora do try/catch a tela quebraria em janela
 * anônima ou com armazenamento bloqueado.
 */
const CHAVE_ABERTO = 'grafico-diario-aberto';

function lerAberto(): boolean {
  try {
    return localStorage.getItem(CHAVE_ABERTO) === '1';
  } catch {
    return false;
  }
}

export function DailyChart({ series, dias }: Props) {
  // Recolhido por padrão: a tela abre no que responde "quanto rendeu" (os
  // totais). O gráfico responde "quando", que é a segunda pergunta.
  const [aberto, setAberto] = useState(lerAberto);
  const [tabela, setTabela] = useState(false);
  const [ativo, setAtivo] = useState<number | null>(null);
  const hachura = useId().replace(/:/g, '');
  const corpoId = `grafico-diario-${useId().replace(/:/g, '')}`;

  function alternar() {
    const proximo = !aberto;
    setAberto(proximo);
    try {
      localStorage.setItem(CHAVE_ABERTO, proximo ? '1' : '0');
    } catch {
      /* sem armazenamento: vale para esta sessão */
    }
  }

  if (series.length === 0) return null;

  const largura = 760;
  const faixa = (largura - EIXO_X - 8) / series.length;
  // Barra mais estreita que a fatia: o vão de ~2px é o que separa dias vizinhos
  // sem precisar de borda.
  const larguraBarra = Math.max(3, Math.min(18, faixa - 3));

  const maxComissao = Math.max(...series.map((d) => d.comissaoCents), 1);
  const maxConv = Math.max(...series.map((d) => d.conversoes + d.canceladas), 1);

  const totalComissao = series.reduce((t, d) => t + d.comissaoCents, 0);
  const totalConv = series.reduce((t, d) => t + d.conversoes, 0);
  const totalCanc = series.reduce((t, d) => t + d.canceladas, 0);
  const diasComVenda = series.filter((d) => d.conversoes > 0).length;

  const x = (i: number) => EIXO_X + i * faixa + (faixa - larguraBarra) / 2;
  const dia = (iso: string) => iso.slice(8, 10);
  const mes = (iso: string) => iso.slice(5, 7);

  /** Rótulos do eixo x: um a cada ~5 dias, senão 31 datas se sobrepõem. */
  const passo = Math.max(1, Math.round(series.length / 6));

  return (
    <section className="card" style={{ padding: 18, marginBottom: 18 }}>
      {/* O cabeçalho INTEIRO é o alvo do clique, não um ícone de 12px ao lado
          do título — e o resumo fica visível fechado, senão a faixa recolhida
          não diria nada. */}
      <button
        type="button"
        className="ghost"
        aria-expanded={aberto}
        aria-controls={corpoId}
        onClick={alternar}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          textAlign: 'left',
          padding: 0,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            flex: '0 0 auto',
            color: 'var(--ink-3)',
            fontSize: 11,
            // Gira em vez de trocar de glifo: o movimento diz que é a MESMA
            // seção abrindo. `prefers-reduced-motion` desliga em theme-base.
            transform: aberto ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.16s ease',
            display: 'inline-block',
          }}
        >
          ▶
        </span>
        <span
          className="section-title"
          style={{ margin: 0, flex: '0 0 auto', color: 'var(--ink)' }}
        >
          Por dia
        </span>
        <span className="muted" style={{ flex: 1, fontSize: 12.5, fontWeight: 400 }}>
          {totalConv} conversão(ões) em {diasComVenda} dia(s) ·{' '}
          {formatBRL(totalComissao)} de comissão
          {totalCanc > 0 && ` · ${totalCanc} cancelada(s), fora da soma`}
        </span>
        <span className="muted" style={{ flex: '0 0 auto', fontSize: 12 }}>
          {aberto ? 'recolher' : `ver ${dias} dias`}
        </span>
      </button>

      {aberto && (
        <div id={corpoId} style={{ marginTop: 14 }}>
          <div
            className="row"
            style={{ justifyContent: 'flex-end', marginBottom: 10 }}
          >
            {/* Fica DENTRO da área expandida: fechado, não haveria o que
                alternar. */}
            <button
              type="button"
              className="ghost"
              style={{ flex: '0 0 auto' }}
              aria-pressed={tabela}
              onClick={() => setTabela((v) => !v)}
            >
              {tabela ? 'Ver gráfico' : 'Ver tabela'}
            </button>
          </div>

          {tabela ? (
        // A tabela não é acessório: é o caminho de leitura para quem usa leitor
        // de tela, para quem imprime e para conferir número exato.
        <div style={{ overflowX: 'auto' }}>
          <table className="offer-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Dia</th>
                <th style={{ width: 110 }}>Conversões</th>
                <th style={{ width: 110 }}>Canceladas</th>
                <th style={{ width: 130 }}>Comissão</th>
                <th style={{ width: 130 }}>Vendido</th>
              </tr>
            </thead>
            <tbody>
              {series
                .filter((d) => d.conversoes > 0 || d.canceladas > 0)
                .map((d) => (
                  <tr key={d.dia}>
                    <td>{`${dia(d.dia)}/${mes(d.dia)}`}</td>
                    <td className="num">{d.conversoes || '—'}</td>
                    <td className="num">{d.canceladas || '—'}</td>
                    <td className="num">
                      {d.comissaoCents > 0 ? formatBRL(d.comissaoCents) : '—'}
                    </td>
                    <td className="num">
                      {d.vendidoCents > 0 ? formatBRL(d.vendidoCents) : '—'}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <svg
            viewBox={`0 0 ${largura} ${ALTURA * 2 + 20}`}
            width="100%"
            role="img"
            aria-label={
              `Comissão e conversões por dia nos últimos ${dias} dias. ` +
              `Total: ${totalConv} conversões, ${formatBRL(totalComissao)}. ` +
              'Use "Ver tabela" para os números exatos.'
            }
            style={{ display: 'block', overflow: 'visible' }}
            onMouseLeave={() => setAtivo(null)}
          >
            <defs>
              {/* Hachura a 45°: é o encoding secundário que substitui o
                  vermelho, e funciona em impressão e alto contraste. */}
              <pattern
                id={hachura}
                width="5"
                height="5"
                patternTransform="rotate(45)"
                patternUnits="userSpaceOnUse"
              >
                <rect width="5" height="5" fill="var(--surface-2)" />
                <line
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="5"
                  stroke="var(--ink-3)"
                  strokeWidth="1.6"
                />
              </pattern>
            </defs>

            {/* ---------------------------------------- comissão por dia */}
            <text x="0" y={TITULO_Y} fill="var(--ink-3)" fontSize="10">
              Comissão por dia
            </text>
            {[0, 0.5, 1].map((f) => {
              const y = BASE - f * (BASE - TOPO);
              return (
                <g key={f}>
                  <line
                    x1={EIXO_X}
                    y1={y}
                    x2={largura - 8}
                    y2={y}
                    stroke="var(--chart-grid)"
                    strokeWidth="1"
                  />
                  <text
                    x={EIXO_X - 6}
                    y={y + 3}
                    textAnchor="end"
                    fill="var(--ink-3)"
                    fontSize="9.5"
                  >
                    {f === 0 ? '0' : formatBRL(maxComissao * f).replace('R$', '').trim()}
                  </text>
                </g>
              );
            })}
            {series.map((d, i) => {
              if (d.comissaoCents <= 0) return null;
              const h = ((BASE - TOPO) * d.comissaoCents) / maxComissao;
              return (
                <rect
                  key={d.dia}
                  x={x(i)}
                  y={BASE - h}
                  width={larguraBarra}
                  height={h}
                  rx="2"
                  fill="var(--chart-fill)"
                  opacity={ativo === null || ativo === i ? 1 : 0.35}
                />
              );
            })}

            {/* ---------------------------------------- conversões por dia */}
            <g transform={`translate(0, ${ALTURA + 14})`}>
              <text x="0" y={TITULO_Y} fill="var(--ink-3)" fontSize="10">
                Conversões por dia
              </text>
              {[0, 1].map((f) => {
                const y = BASE - f * (BASE - TOPO);
                return (
                  <g key={f}>
                    <line
                      x1={EIXO_X}
                      y1={y}
                      x2={largura - 8}
                      y2={y}
                      stroke="var(--chart-grid)"
                      strokeWidth="1"
                    />
                    <text
                      x={EIXO_X - 6}
                      y={y + 3}
                      textAnchor="end"
                      fill="var(--ink-3)"
                      fontSize="9.5"
                    >
                      {f === 0 ? '0' : maxConv}
                    </text>
                  </g>
                );
              })}
              {series.map((d, i) => {
                const unidade = (BASE - TOPO) / maxConv;
                const hOk = d.conversoes * unidade;
                const hCanc = d.canceladas * unidade;
                const dim = ativo === null || ativo === i ? 1 : 0.35;
                return (
                  <g key={d.dia} opacity={dim}>
                    {d.conversoes > 0 && (
                      <rect
                        x={x(i)}
                        y={BASE - hOk}
                        width={larguraBarra}
                        height={hOk}
                        rx="2"
                        fill="var(--chart-fill)"
                      />
                    )}
                    {d.canceladas > 0 && (
                      <rect
                        x={x(i)}
                        // 2px de folga entre os segmentos: o vão é o que separa
                        // "contou" de "não contou" sem depender da cor.
                        y={BASE - hOk - hCanc - (d.conversoes > 0 ? 2 : 0)}
                        width={larguraBarra}
                        height={hCanc}
                        rx="2"
                        fill={`url(#${hachura})`}
                        stroke="var(--line-strong)"
                        strokeWidth="0.8"
                      />
                    )}
                  </g>
                );
              })}

              {/* Eixo x compartilhado pelos dois gráficos. */}
              <line
                x1={EIXO_X}
                y1={BASE}
                x2={largura - 8}
                y2={BASE}
                stroke="var(--line)"
                strokeWidth="1"
              />
              {series.map((d, i) =>
                i % passo === 0 || i === series.length - 1 ? (
                  <text
                    key={d.dia}
                    x={x(i) + larguraBarra / 2}
                    y={BASE + 14}
                    textAnchor="middle"
                    fill="var(--ink-3)"
                    fontSize="9.5"
                  >
                    {`${dia(d.dia)}/${mes(d.dia)}`}
                  </text>
                ) : null,
              )}
            </g>

            {/* Alvos de mouse por dia, acima de tudo e mais largos que a barra:
                acertar uma barra de 3px seria impossível. */}
            {series.map((d, i) => (
              <rect
                key={`hit-${d.dia}`}
                x={EIXO_X + i * faixa}
                y={0}
                width={faixa}
                height={ALTURA * 2 + 6}
                fill="transparent"
                onMouseEnter={() => setAtivo(i)}
              />
            ))}
          </svg>

          {/* Legenda: dois encodings na tela, então ela é obrigatória. */}
          <div
            className="row"
            style={{ gap: 16, marginTop: 10, alignItems: 'center' }}
          >
            <span
              className="muted"
              style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: 2,
                  background: 'var(--chart-fill)',
                  display: 'inline-block',
                }}
              />
              concluída
            </span>
            <span
              className="muted"
              style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: 2,
                  border: '1px solid var(--line-strong)',
                  background:
                    'repeating-linear-gradient(45deg, var(--ink-3) 0 1.4px, var(--surface-2) 1.4px 4px)',
                  display: 'inline-block',
                }}
              />
              cancelada
            </span>

            {ativo !== null && series[ativo] && (
              <span style={{ marginLeft: 'auto', flex: '0 0 auto', fontSize: 12.5 }}>
                <strong>
                  {dia(series[ativo].dia)}/{mes(series[ativo].dia)}
                </strong>{' '}
                <span className="muted">
                  {series[ativo].conversoes} conv ·{' '}
                  {formatBRL(series[ativo].comissaoCents)}
                  {series[ativo].canceladas > 0 &&
                    ` · ${series[ativo].canceladas} cancelada(s)`}
                </span>
              </span>
            )}
          </div>
        </div>
          )}
        </div>
      )}
    </section>
  );
}
